// src/main.ts
import { createApp } from 'vue'
import App from '@/App.vue'

import '@mdi/font/css/materialdesignicons.min.css'
import './styles/global.css'

import { VueQueryPlugin } from '@tanstack/vue-query'
import CountryFlag from 'vue-country-flag-next'
import { vMaska } from 'maska/vue'
import { i18n } from '@/plugins/i18n'
import { pinia } from '@/plugins/pinia'
import './plugins/vee-validate'
import './plugins/zod'
import { registerGlobalComponents } from '@/components/index'
import { VueTelInput } from 'vue-tel-input'
import { vPermission } from '@/directives/vPermission'
import {
  ToastPlugin,
  vueToastificationOptions,
} from '@/plugins/vue-toastification'
import { vuetify } from '@/plugins/vuetify'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

import KeycloakService from '@/security/keycloakService'
import useAuthStore from '@/store/auth'
import type { KeycloakTokenParsed } from 'keycloak-js'

import appRouter from '@/router'
// ⚠️ agora é ARRAY de rotas públicas:
import { guestPublicRoutes } from '@/modules/guest/routes/guest-public'

const isGuestEventsHost =
  /^guest-events(\.|-)/.test(location.hostname) ||
  location.hostname === 'guest-events.localhost' ||
  import.meta.env.VITE_FORCE_GUEST_PUBLIC === '1'

const isGuestEventsPath = window.location.pathname.startsWith('/guest-events')

// Quando abrir diretamente um link público ou estiver num host público,
// rodamos em "modo público" (sem Keycloak, só rotas públicas).
const isPublicRuntime = isGuestEventsHost || isGuestEventsPath

async function setup(user?: KeycloakTokenParsed) {
  const app = createApp(App)

  app.use(vuetify)
  app.use(pinia)
  app.use(i18n)

  if (isPublicRuntime) {
    // cria um ÚNICO router usando APENAS as rotas públicas
    const { createRouter, createWebHistory } = await import('vue-router')
    const guestRouter = createRouter({
      history: createWebHistory(import.meta.env.BASE_URL),
      routes: guestPublicRoutes, // <- array de RouteRecordRaw
      scrollBehavior: () => ({ top: 0 }),
    })
    app.use(guestRouter)
  } else {
    // modo privado (app autenticado): usa o router principal
    app.use(appRouter)
  }

  app.use(ToastPlugin, vueToastificationOptions)
  app.use(VueQueryPlugin)

  app.component('CountryFlag', CountryFlag)
  app.component('VueTelInput', VueTelInput)
  app.directive('maska', vMaska)
  app.component('QuillEditor', QuillEditor)
  app.directive('permission', vPermission)
  registerGlobalComponents(app)

  app.mount('#app')

  // snapshot auth no store (somente relevante no modo privado)
  const store = useAuthStore()
  if (user) {
    store.setUser(user)
  } else {
    store.setUser(undefined)
    store.setToken(undefined)
  }
}

if (isPublicRuntime) {
  // público: sem login
  setup()
} else {
  // privado: autentica pelo Keycloak e depois sobe o app
  KeycloakService.CallLogin(({ user }) => {
    if (user) setup(user)
    else setup()
  })
}
